import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Divider, Skeleton } from 'antd';
import { TbCloudLock, TbFolderBolt, TbLogout } from 'react-icons/tb';

import { useEntitlements } from '@/hooks/useEntitlements';
import { WorkspaceIdentityInput, WorkspaceIdentityPortalLinkInput, workspaceService } from '@/services/database';
import { LearnMoreFooter } from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { WorkspaceIdentity } from '@/types';

import AdvancedSettingsViewHeader from '../components/AdvancedSettingsViewHeader';
import DirectorySyncSection from './components/DirectorySyncSection';
import ForceLogoutSection from './components/ForceLogoutSection';
import SessionDurationSection from './components/SessionDurationSection';

const Identity: FC = (): JSX.Element => {
    const { selectedUser: user } = useUserStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { hasFeature } = useEntitlements();

    const [identity, setIdentity] = useState<WorkspaceIdentity | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [openingPortalIntent, setOpeningPortalIntent] = useState<WorkspaceIdentityPortalLinkInput['intent'] | null>(
        null,
    );
    const [forcingLogout, setForcingLogout] = useState(false);

    const isEnterprise = hasFeature('workspaceIdentity');

    const fetchIdentity = async () => {
        if (!workspace?.id) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const response = await workspaceService.getIdentity(workspace.id);

            setIdentity(response);
        } catch (error) {
            captureException(error);
            toast('Unable to load identity settings', {
                icon: <TbCloudLock size='1rem' />,
                description: workspace.name,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchIdentity();
    }, [workspace?.id]);

    const updateIdentity = async (input: WorkspaceIdentityInput, toastLabel: string) => {
        if (!workspace?.id) {
            return;
        }

        const previousIdentity = identity;
        const optimisticIdentity = identity ? { ...identity, ...input } : null;

        if (optimisticIdentity) {
            setIdentity(optimisticIdentity);
        }

        setSaving(true);

        try {
            const response = await workspaceService.updateIdentity(workspace.id, input);

            setIdentity(response);
            toast(toastLabel, {
                icon: <TbCloudLock size='1rem' />,
                description: workspace.name,
            });
        } catch (error) {
            captureException(error);
            setIdentity(previousIdentity);
            toast('Unable to update identity settings', {
                icon: <TbCloudLock size='1rem' />,
                description: workspace.name,
            });
        } finally {
            setSaving(false);
        }
    };

    const openWorkOSPortal = async (intent: WorkspaceIdentityPortalLinkInput['intent']) => {
        if (!workspace?.id) {
            return;
        }

        setOpeningPortalIntent(intent);

        try {
            const response = await workspaceService.createIdentityPortalLink(workspace.id, { intent });

            window.location.assign(response.link);
        } catch (error) {
            captureException(error);
            toast('Unable to open WorkOS setup', {
                icon: <TbFolderBolt size='1rem' />,
                description: workspace.name,
            });
        } finally {
            setOpeningPortalIntent(null);
        }
    };

    const forceLogout = async () => {
        if (!workspace?.id) {
            return;
        }

        setForcingLogout(true);

        try {
            const response = await workspaceService.forceLogoutMembers(workspace.id);

            setIdentity((current) =>
                current
                    ? {
                          ...current,
                          lastSessionRevocationAt: new Date().toISOString(),
                          lastSessionRevocationByUserId: user?.id,
                      }
                    : current,
            );
            toast('Forced workspace users to log out', {
                icon: <TbLogout size='1rem' />,
                description: `${response.revokedSessionCount.toLocaleString()} active sessions revoked.`,
            });
        } catch (error) {
            captureException(error);
            toast('Unable to force users to log out', {
                icon: <TbLogout size='1rem' />,
                description: workspace.name,
            });
        } finally {
            setForcingLogout(false);
        }
    };

    if (loading) {
        return <Skeleton active paragraph={{ rows: 8 }} title />;
    }

    return (
        <>
            <AdvancedSettingsViewHeader
                description='Manage SAML SSO, directory provisioning, session duration, and workspace-wide session revocation.'
                title='Identity'
            />
            <DirectorySyncSection
                identity={identity}
                isEnterprise={isEnterprise}
                openingPortalIntent={openingPortalIntent}
                openWorkOSPortal={openWorkOSPortal}
                saving={saving}
                updateIdentity={updateIdentity}
            />
            <Divider style={{ margin: '1rem 0' }} />
            <SessionDurationSection
                identity={identity}
                isEnterprise={isEnterprise}
                saving={saving}
                updateIdentity={updateIdentity}
            />
            <Divider style={{ margin: '1rem 0' }} />
            <ForceLogoutSection
                forceLogout={forceLogout}
                forcingLogout={forcingLogout}
                identity={identity}
                isEnterprise={isEnterprise}
            />
            <Divider style={{ margin: '1rem 0' }} />
            <LearnMoreFooter topic={<>identity preferences</>} />
        </>
    );
};

export default Identity;
